import React, { useState, useEffect, useRef } from 'react';
//import { useAuth0 } from '../../hooks/useAuth0WithImpersonation';
import { useAuth0 } from '@auth0/auth0-react';
import { User, ChevronDown, Menu, Home, Info, Circle, HelpCircle, Users, LogOut } from 'lucide-react';
import './Header.css';

const Header = () => {
  const { isAuthenticated, loginWithRedirect, logout, user, isLoading } = useAuth0();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isResourcesOpen, setIsResourcesOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const headerRef = useRef(null);
  const resourcesRef = useRef(null);
  const profileRef = useRef(null);

  // Get current path to determine active menu item
  const currentPath = window.location.pathname;

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
      if (resourcesRef.current && !resourcesRef.current.contains(event.target)) {
        setIsResourcesOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleResources = () => {
    setIsResourcesOpen(!isResourcesOpen);
    if (isProfileOpen) setIsProfileOpen(false);
  };

  const toggleProfile = () => {
    setIsProfileOpen(!isProfileOpen);
    if (isResourcesOpen) setIsResourcesOpen(false);
  };

  const handleLogin = () => {
    loginWithRedirect();
  };

  const handleLogout = () => {
    logout({ returnTo: window.location.origin });
  };

  const handleJoinNow = () => {
    loginWithRedirect({
      authorizationParams: {
        screen_hint: 'signup',
      }
    });
  };

  // Check if a menu item is active - exactly match the path
  const isActive = (path) => {
    if (path === '/') {
      return currentPath === path;
    }
    return currentPath === path;
  };

  // Show skeleton loader while Auth0 is initializing
  if (isLoading) {
    return (
      <header className="header-main">
        <div className="header-container">
          <div className="header-logo">Circledin</div>
          <nav className="header-nav-menu">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="header-nav-item header-skeleton-item">
                <div className="header-skeleton-icon"></div>
                <div className="header-skeleton-text"></div>
              </div>
            ))}
          </nav>
          <div className="header-actions">
            <div className="header-skeleton-button"></div>
          </div>
        </div>
      </header>
    );
  }

  return (
    <>
      {/* Overlay for mobile menu */}
      <div 
        className={`header-mobile-overlay ${isMobileMenuOpen ? 'active' : ''}`}
        onClick={() => setIsMobileMenuOpen(false)}
      />
      
      <header ref={headerRef} className={`header-main ${isScrolled ? 'header-scrolled' : ''}`}>
        <div className="header-container">
          <div className="header-logo">Circledin</div>
          <nav className={`header-nav-menu ${isMobileMenuOpen ? 'header-mobile-menu-open' : ''}`}>
            <a 
              href="/" 
              className={`header-nav-link header-nav-item ${isActive('/') ? 'header-nav-active' : ''}`}
            >
              <Home size={20} /> 
              <span className="header-nav-text">Home</span>
            </a>
            <a 
              href="/how-it-works" 
              className={`header-nav-link header-nav-item ${isActive('/how-it-works') ? 'header-nav-active' : ''}`}
            >
              <Info size={20} /> 
              <span className="header-nav-text">How It Works</span>
            </a>
            <a 
              href="/all-circles" 
              className={`header-nav-link header-nav-item ${isActive('/all-circles') ? 'header-nav-active' : ''}`}
            >
              <Circle size={20} /> 
              <span className="header-nav-text">All Circles</span>
            </a>
            
            <div className="header-nav-dropdown" ref={resourcesRef}>
              <button 
                className="header-nav-link header-dropdown-toggle header-nav-item" 
                onClick={toggleResources}
                aria-expanded={isResourcesOpen}
              >
                <HelpCircle size={20} />
                <span className="header-nav-text">Resources</span>
                <ChevronDown 
                  size={16} 
                  className={`header-chevron ${isResourcesOpen ? 'open' : ''}`} 
                />
              </button>
              <div className={`header-dropdown-menu ${isResourcesOpen ? 'show' : ''}`}>
                <a 
                  href="https://circledin.zendesk.com/hc/en-us" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="header-dropdown-item"
                >
                  <HelpCircle size={18} />
                  <span>Help Center</span>
                </a>
                <a href="/how-it-works#faq" className="header-dropdown-item">
                  <Info size={18} />
                  <span>FAQ</span>
                </a>
              </div>
            </div>

            <a 
              href="/about-us" 
              className={`header-nav-link header-nav-item ${isActive('/about-us') ? 'header-nav-active' : ''}`}
            >
              <Users size={20} />
              <span className="header-nav-text">About Us</span>
            </a>

            {isAuthenticated ? (
              <div className="header-nav-dropdown" ref={profileRef}>
                <button
                  className="header-nav-link header-dropdown-toggle header-nav-item"
                  onClick={toggleProfile}
                  aria-expanded={isProfileOpen}
                >
                  <User size={20} />
                  <span className="header-nav-text">{user?.name || 'Profile'}</span>
                  <ChevronDown 
                    size={16} 
                    className={`header-chevron ${isProfileOpen ? 'open' : ''}`} 
                  />
                </button>
                <div className={`header-dropdown-menu ${isProfileOpen ? 'show' : ''}`}>
                  <a href="/my-profile" className="header-dropdown-item">
                    <User size={18} />
                    <span>My Profile</span>
                  </a>
                  <a href="/my-circles" className="header-dropdown-item">
                    <Circle size={18} />
                    <span>My Circles</span>
                  </a>
                  <button onClick={handleLogout} className="header-dropdown-item header-dropdown-item-danger">
                    <LogOut size={18} />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            ) : (
              <>
                <a 
                  href="#" 
                  onClick={(e) => {
                    e.preventDefault();
                    handleLogin();
                  }} 
                  className={`header-nav-link header-nav-item ${isActive('/login') ? 'header-nav-active' : ''}`}
                >
                  <User size={20} />
                  <span className="header-nav-text">Login</span>
                </a>
                
                <button 
                  onClick={handleJoinNow}
                  className="header-mobile-join-button"
                >
                  <User size={20} />
                  <span>Join Now</span>
                </button>
              </>
            )}
          </nav>
          <div className="header-actions">
            {!isAuthenticated && !isLoading && (
              <button className="header-join-button" onClick={handleJoinNow}>
                Join Now
              </button>
            )}
            <button
              className="header-mobile-menu-toggle"
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;